import { useCallback, useEffect, useMemo } from 'react';

import { PREFERRED_UNIT_SYSTEMS, PreferredUnitSystem } from '@frontend/api';

import { DEFAULT_UNITS_BY_PREFERRED_SYSTEM } from 'constants/unitOfMeasure';

export const inferDisplayUnits = (preferredUnitSystem: PreferredUnitSystem) =>
  DEFAULT_UNITS_BY_PREFERRED_SYSTEM[preferredUnitSystem];

type UseViewerUnitSystemPreferenceReturn = {
  preferredUnitSystem: PreferredUnitSystem;
  setPreferredUnitSystem: (unitSystem: PreferredUnitSystem) => void;
};

/**
 * Hook that manages the user's personal unit system preference in localStorage
 * Initializes with the user group's preference from UnitSystemContext
 *
 * @returns Object containing current preference, setter, and reset function
 */
export const useViewerUnitSystemPreference = ({
  preferredUnitSystem,
  setPreferredUnitSystem,
  storageKey,
}: {
  preferredUnitSystem: PreferredUnitSystem;
  setPreferredUnitSystem: (unitSystem: PreferredUnitSystem) => void;
  storageKey: string;
}): UseViewerUnitSystemPreferenceReturn => {
  // Get group preference from context (fallback to Imperial if context not available)
  const groupPreference = useMemo(() => {
    return preferredUnitSystem ?? PREFERRED_UNIT_SYSTEMS.IMPERIAL;
  }, [preferredUnitSystem]);

  // Initialize localStorage value on first load
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        // Initialize with group preference if no stored value
        localStorage.setItem(storageKey, groupPreference);
      } else {
        setPreferredUnitSystem(stored as PreferredUnitSystem);
      }
    } catch (error) {
      console.warn('localStorage not available:', error);
    }
  });

  const handleSetPreferredUnitSystem = useCallback(
    (unitSystem: PreferredUnitSystem) => {
      try {
        localStorage.setItem(storageKey, unitSystem);
        setPreferredUnitSystem(unitSystem);
      } catch (error) {
        console.warn(
          'Failed to save unit system preference to localStorage:',
          error,
        );
      }
    },
    [setPreferredUnitSystem, storageKey],
  );

  // Read directly from localStorage, falling back to group preference
  const getCurrentPreference = (): PreferredUnitSystem => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return stored as PreferredUnitSystem;
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }

    return groupPreference;
  };

  const currentPreference = getCurrentPreference();

  return {
    preferredUnitSystem: currentPreference,
    setPreferredUnitSystem: handleSetPreferredUnitSystem,
  };
};
